import {Button} from '@deviceinsight/ng-ui-basic-components';

import {Trans, DashboardWidgetConfig, WidgetProps} from '@deviceinsight/ng-ui-scale-lib/api';
import SelectAssetButton from '../../common/components/SelectAssetButton';
import SelectKpiButton from '../../common/components/SelectKpiButton';
import EnumDropdown from '../../common/components/EnumDropdown';
import {KpiQuery} from '../../common/kpiUtils';
import ZoneEdit from './ZoneEdit';
import useAssetsByUrns from '../../common/hooks/useAssetsByUrns';
import TimeConfig from '../../common/components/TimeConfig';

type Props = {
	context: WidgetProps['context'];
	config: DashboardWidgetConfig;
	onChangeConfig: (arg0: DashboardWidgetConfig) => void;
	setSelectingAsset: () => void;
	setSelectingKpi: (assetUrn: string) => void;
};
type Zone = {
	color: string;
	value: string;
};
const ZONE_STEP = 100;

const reCalculateZonesAndAddZone = (zones: Array<Zone>): Array<Zone> => {
	const updatedZones = [...zones];

	if (zones.length > 1) {
		updatedZones.splice(updatedZones.length - 1, 0, {
			color: '#000000',
			value: `${parseInt(updatedZones[updatedZones.length - 2].value) + ZONE_STEP}`
		});
	} else {
		updatedZones.splice(0, 0, {
			color: '#000000',
			value: '100'
		});
	}

	return updatedZones;
};

const KpiSingleValueColumnWidgetConfigContent = ({
	context,
	config,
	config: {settings},
	onChangeConfig,
	setSelectingAsset,
	setSelectingKpi
}: Props) => {
	const isAssetGroupContext = context.type === 'assetGroup';
	const kpiButtonDisabled = isAssetGroupContext && !settings.assetUrn;
	const assets = useAssetsByUrns(settings.assetUrn ? [settings.assetUrn] : []);
	const {zones} = settings;
	return (
		<div className="di form">
			<div className="fields">
				<div className="field">
					<div className="standard wide field">
						<label>
							<Trans i18nKey="dashboard.widgets.config.boxHeadline">Headline</Trans>
						</label>
						<input
							data-testid="title-input"
							type="text"
							value={config.title}
							onChange={(e) => onChangeConfig({...config, title: e.target.value})}
						/>
					</div>
				</div>
				<div className="field">
					<label>
						<Trans i18nKey="dashboard.widget.kpiaggredatedchart.label">Label</Trans>
					</label>
					<input
						data-testid="label-input"
						type="text"
						value={settings.label}
						onChange={(e) =>
							onChangeConfig({...config, settings: {...config.settings, label: e.target.value}})
						}
					/>
				</div>
			</div>
			<div className="fields">
				{isAssetGroupContext && (
					<div className="field">
						<label>
							<Trans i18nKey="dashboard.widgets.kpisinglevalue.config.asset">Asset</Trans>
						</label>
						<SelectAssetButton assetName={assets[0]?.name ?? ''} selectAsset={setSelectingAsset} />
					</div>
				)}
				<div className="field">
					<label>
						<Trans i18nKey="dashboard.widgets.kpisinglevalue.config.kpiname">Kpi Name</Trans>
					</label>
					<SelectKpiButton
						kpiName={settings.kpiName}
						selectKpi={() =>
							setSelectingKpi(context.type === 'asset' ? context.asset.urn : settings.assetUrn)
						}
						disabled={kpiButtonDisabled}
					/>
				</div>
				<div className="field">
					<label>
						<Trans i18nKey="dashboard.widgets.kpisinglevalue.config.unite">Unit</Trans>
					</label>
					<input
						data-testid="unit-input"
						type="text"
						value={settings.unit || ''}
						onChange={({target: {value}}) =>
							onChangeConfig({...config, settings: {...config.settings, unit: value}})
						}
					/>
				</div>
				<div className="field">
					<label>
						<Trans i18nKey="dashboard.widgets.kpisinglevalue.config.aggregationtype">
							Aggregation type
						</Trans>
					</label>
					<EnumDropdown
						data-testid="aggregation-type-select"
						toFilter={['ALL']}
						options={KpiQuery}
						value={settings.aggregationType}
						onChange={(aggregationType) =>
							onChangeConfig({...config, settings: {...config.settings, aggregationType}})
						}
					/>
				</div>
			</div>
			<TimeConfig
				periodTimeframe={settings.periodTimeframe}
				periodType={settings.periodType}
				periodCount={settings.periodCount}
				customPeriodFrom={settings.customPeriodFrom}
				customPeriodTo={settings.customPeriodTo}
				workingShiftEnabled={settings.workingShiftEnabled}
				workingShiftTimeFrom={settings.workingShiftTimeFrom}
				workingShiftTimeTo={settings.workingShiftTimeTo}
				workingShiftDays={settings.workingShiftDays}
				onPeriodTimeframePeriodTypeChange={(periodTimeframe, periodType) =>
					onChangeConfig({...config, settings: {...config.settings, periodTimeframe, periodType}})
				}
				onPeriodCountChange={(periodCount) =>
					onChangeConfig({...config, settings: {...config.settings, periodCount}})
				}
				onCustomPeriodChange={(customPeriodFrom, customPeriodTo) =>
					onChangeConfig({...config, settings: {...config.settings, customPeriodFrom, customPeriodTo}})
				}
				onWorkingShiftEnabledChange={(workingShiftEnabled) =>
					onChangeConfig({...config, settings: {...config.settings, workingShiftEnabled}})
				}
				onWorkingShiftTimeChange={(workingShiftTimeFrom, workingShiftTimeTo) =>
					onChangeConfig({
						...config,
						settings: {...config.settings, workingShiftTimeFrom, workingShiftTimeTo}
					})
				}
				onWorkingShiftDaysChange={(workingShiftDays) =>
					onChangeConfig({...config, settings: {...config.settings, workingShiftDays}})
				}
			/>
			<div className="fields">
				<div className="field">
					<label>
						<Trans i18nKey="dashboard.widgets.kpisinglevalue.config.zones">Zones</Trans>
					</label>
					<div className="kpi-zones">
						{zones.map((zone: any, index: number) => (
							<ZoneEdit
								zone={zone}
								index={index}
								zones={zones}
								key={`zone_${index}`}
								onZonesChange={(zones) =>
									onChangeConfig({...config, settings: {...config.settings, zones}})
								}
							/>
						))}
					</div>
				</div>
			</div>
			{zones.length < 5 && (
				<div className="fields">
					<div className="field">
						<Button
							onClick={() =>
								onChangeConfig({
									...config,
									settings: {...config.settings, zones: reCalculateZonesAndAddZone(zones)}
								})
							}>
							<Trans i18nKey="dashboard.widgets.kpisinglevalue.config.addzone">
								Add color zone (max 5)
							</Trans>
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};

export default KpiSingleValueColumnWidgetConfigContent;
